import {AfterViewInit, Component, HostListener, Inject, Input, OnInit, QueryList, ViewChildren,} from '@angular/core';
import {ActivatedRoute, Data, Router} from "@angular/router";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent, CurrentItemType} from "@components";
import {
  AdditionalItemDiscount,
  ApplicationFormSettings, CommercialItemDiscount,
  Competitor,
  CompetitorInfoDTO,
  CompetitorRegistrationDto, CompetitorRegistrationResultDto,
  Country,
  EventItem,
  EventPaymentType, FormFieldError,
  Lookups,
  Race,
  RaceAdditionalPricingItem,
  RaceCommercialItem,
  RaceRegistrationRequestDto,
  RaceRegistrationResultDto,
  RaceRegResult,
  UserDetail,
  WspayConfirmation,
  PaymentStatus
} from "@model";
import {AuthenticationService, RaceService, RegisterToRaceError, TOAST_STATE, ToastService} from "@services";
import {RegisterToRaceItemComponent} from "./register-to-race-item/register-to-race-item.component";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from "@angular/forms";
import {pairwise} from "rxjs";
import {LANDING_ROUTE, PRIVATE_EVENT_CALENDAR_ROUTE} from "../../../app-routing.module";
import {DOCUMENT} from "@angular/common";

@Component({
  selector: 'app-register-to-race-details',
  templateUrl: './register-to-race.component.html',
  styleUrls: ['./register-to-race.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})
export class RegisterToRaceComponent extends AbstractComponent implements OnInit, AfterViewInit {
  isAfterViewInitPassed: boolean = false;

  @ViewChildren(RegisterToRaceItemComponent) registerToRaceComponentItems!: QueryList<RegisterToRaceItemComponent>;

  @Input() race: Race | undefined;
  @Input() competitor: Competitor = new Competitor();
  @Input() countries: Country[] = [];
  @Input() currentUserDetails: UserDetail | undefined;
  @Input('raceCommercialItems') raceComItems: RaceCommercialItem[] = [];
  @Input('raceAdditionalItems') raceAddItems: RaceAdditionalPricingItem[] = [];
  @Input() applicationFormSettings: ApplicationFormSettings | undefined;
  @Input('sizes') tshirtSizes: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input() transports: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() disciplines: Lookups[] = [];
  @Input('eventItem') event: EventItem | undefined;
  @Input() wspayConfirmation: WspayConfirmation | undefined;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() isPreviewComponent: boolean = false;
  @Input() isManagement: boolean = false;

  eventCurrencyLabel: string | undefined;
  registerToRaceUserTypeState: RegisterToRaceUserType = RegisterToRaceUserType.GUEST
  registerToRaceFormType: RegisterToRaceFormType = RegisterToRaceFormType.REGISTRATION_INFO;
  registerForMySelf: boolean = true;
  registerToRaceResponse: RaceRegistrationResultDto | undefined;
  isSmallerThanXXL: boolean = false;
  registerHelper: RegisterHelper | undefined;
  registrationWithoutPayment:boolean = false;

  inputForm = new FormGroup({
    coupons: this.formBuilder.array([]),
    teamCoupon: new FormControl(),
    validTeamCoupon: new FormControl()
  });

  constructor(private route: ActivatedRoute, public formBuilder: FormBuilder, private raceService: RaceService,
              protected translocoService: TranslocoService, private router: Router,@Inject(DOCUMENT) private document: Document,
              private toastService: ToastService, public authenticationService:AuthenticationService) {
    super();
    this.checkWindowSize();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      /// to avoid ExpressionChangedAfterItHasBeenCheckedError with this method in template isApplyTeamCouponButtonDisabled
      this.isAfterViewInitPassed = true;
    });
  }

  ngOnInit(): void {
    this.subscribeToRouteDataChange();
    if (this.wspayConfirmation) {
      this.registerToRaceFormType = RegisterToRaceFormType.PAYMENT;
      this.event = this.wspayConfirmation.event;
      this.race = this.wspayConfirmation.race;
    } else {
      this.registerHelper = new RegisterHelper(this.raceComItems ?? [], this.raceAddItems ?? [], this)
      this.registerForMySelf = this.route.snapshot.queryParams['registerToRaceForMyself'] !== "false";
      this.meals = this.filterList(this.applicationFormSettings?.meals, this.meals);
      this.tshirtSizes = this.filterList(this.applicationFormSettings?.tshirts, this.tshirtSizes);
      this.disciplines = this.filterList(this.applicationFormSettings?.disciplines, this.disciplines);
      this.initFormType();
      this.filterInputLists();
      if ((this.event?.racesList?.length ?? 0) > 0) {
        let raceId: number = +this.route.snapshot.queryParams["raceId"];
        this.race = this.event!.racesList!.find((race: Race) => race.id === raceId);
      }
      this.registerHelper = new RegisterHelper(this.route.snapshot.data['raceCommercialItems'] ?? [], this.route.snapshot.data['raceAdditionalItems'] ?? [], this);
      if (this.registerHelper.isTeamRegistration()) {
        for (let i = 0; i < (this.race?.raceSettings?.teamType ?? 0); i++) {
          this.addCoupon(i);
          this.inputForm.controls.teamCoupon.valueChanges.subscribe((value: string) => {
            for (let i = 0; i < this.coupons.length; i++) {
              this.coupons.at(i).get('coupon')?.setValue(value);
            }
            this.inputForm.updateValueAndValidity();
          });
        }
      } else {
        this.addCoupon(0);
      }
      this.setDefaultDataForCurrentUser();
    }
    this.setEventCurrencyLabel(this.event?.currency);
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({isPreview}: Data) => {
        this.isPreviewComponent = isPreview ?? false;
      })
    )
  }

  @HostListener('window:resize')
  onResize() {
    this.checkWindowSize();
  }

  private checkWindowSize() {
    this.isSmallerThanXXL = window.innerWidth < 1400;
  }

  get raceCommercialItems(): RaceCommercialItem[] {
    return this.registerHelper!.raceCommercialItems;
  }

  get raceAdditionalItems(): RaceAdditionalPricingItem[] {
    return this.registerHelper!.raceAdditionalItems;
  }

  get registrations(): CompetitorRegistrationDto[] {
    return this.registerHelper!.registrations;
  }

  /**
   * Filtered list for id's that organiser has selected in race registration settings
   */
  filterInputLists() {
    if ((this.applicationFormSettings?.meals?.length ?? 0) > 0) {
      this.meals = this.filterList(this.applicationFormSettings?.meals, this.meals);
    }
    if ((this.applicationFormSettings?.tshirts?.length ?? 0) > 0) {
      this.tshirtSizes = this.filterList(this.applicationFormSettings?.tshirts, this.tshirtSizes);
    }
    if ((this.applicationFormSettings?.disciplines?.length ?? 0) > 0) {
      this.disciplines = this.filterList(this.applicationFormSettings?.disciplines, this.disciplines);
    }
  }

  filterList(ids: number[] | undefined, list: Lookups[] | undefined): Lookups[] {
    let filteredItems: Lookups[] = [];
    if ((ids?.length ?? 0) > 0 && (list?.length ?? 0) > 0) {
      for (let item of list!) {
        for (let id of ids!) {
          if (item.id === id) {
            filteredItems.push(item);
          }
        }
      }
    }
    return filteredItems;
  }

  private setDefaultDataForCurrentUser() {
    if (this.currentUserDetails && this.registerToRaceUserTypeState === RegisterToRaceUserType.MEMBER) {
      if (this.currentUserDetails?.birthDay) {
        this.currentUserDetails.birthDay = new Date(this.currentUserDetails.birthDay);
      }
      if (!this.currentUserDetails?.birthDay) {
        this.currentUserDetails!.birthDay = new Date();
      }
      if (this.currentUserDetails?.identityCardExpires) {
        this.currentUserDetails.identityCardExpires = new Date(this.currentUserDetails.identityCardExpires);
      }
    }
  }

  private initFormType() {
    if (this.document.URL.includes("preview")) {
      this.registerToRaceUserTypeState = RegisterToRaceUserType.PREVIEW;
    } else if (this.document.URL.includes("race/management")) {
      this.registerToRaceUserTypeState = RegisterToRaceUserType.ORGANISER;
    } else if (this.document.URL.includes("registerToRace")) {
      if (this.authenticationService.isAuthenticated()) {
        this.registerToRaceUserTypeState = RegisterToRaceUserType.MEMBER;
      } else {
        this.registerToRaceUserTypeState = RegisterToRaceUserType.GUEST;
      }
    }
  }

  onContinueWithPayment() {
    if (this.registerToRaceResponse && this.registerToRaceResponse.wsPayForm?.PaymentFormUrl) {
      this.goToWsPay(this.registerToRaceResponse.wsPayForm?.PaymentFormUrl);
    }
  }

  goToWsPay(redirectUrl: string): void {
    window.location.href = redirectUrl;
  }

  confirmRegistration(registrationWithoutPayment:boolean) {
    let allRegistrationItemsAreValid: boolean = true;
    if (this.registerToRaceComponentItems && this.registerToRaceComponentItems.length > 0) {
      for (let i: number = 0; i < this.registerToRaceComponentItems.length; i++) {
        let registerToRaceItem: RegisterToRaceItemComponent | undefined = this.registerToRaceComponentItems.get(i);
        if (!!registerToRaceItem) {
          registerToRaceItem.onRegister();
          if (!registerToRaceItem.inputForm.valid) {
            allRegistrationItemsAreValid = false;
          }
          if (this.registerHelper?.isTeamRegistration() && this.inputForm.controls.validTeamCoupon.value && this.inputForm.controls.validTeamCoupon.value === this.inputForm.controls.teamCoupon.value) {
            registerToRaceItem.competitorRegistration.couponCode = this.inputForm.controls.teamCoupon.value;
          } else if (this.coupons.at(i).get('validCoupon')?.value && this.coupons.at(i).get('validCoupon')?.value === this.coupons.at(i).get('coupon')?.value) {
            registerToRaceItem.competitorRegistration.couponCode = this.coupons.at(i).get('coupon')?.value;
          }
          this.registrations[i] = registerToRaceItem.competitorRegistration;
        }
      }
    }
    if (allRegistrationItemsAreValid) {
      this.register(registrationWithoutPayment);
    }
  }

  register(registrationWithoutPayment:boolean): void {
    if (!this.isPreviewComponent) {
      let raceRegistrationRequestDto: RaceRegistrationRequestDto = new RaceRegistrationRequestDto(this.race?.id, this.registrations);
      if (!this.registerForMySelf && this.registrations.length > 0) {
        raceRegistrationRequestDto.receiptEmail = this.registrations[0].receiptEmail;
      }
      if (this.registerHelper?.isTeamRegistration()) {
        raceRegistrationRequestDto.teamName = this.registrations[0].teamName;
        raceRegistrationRequestDto.teamCommercialItem = this.registrations[0].raceCommercialItem;
      }
      this.raceService.registerToRace(raceRegistrationRequestDto, registrationWithoutPayment, (registerToRaceResponse: RaceRegistrationResultDto) => {
        if (registerToRaceResponse.raceRegResult === RaceRegResult.OK) {
          this.registrationWithoutPayment = registrationWithoutPayment;
          this.registerToRaceResponse = registerToRaceResponse;
          this.registerToRaceFormType = RegisterToRaceFormType.CONFIRMATION;
        } else if (registerToRaceResponse.raceRegResult === RaceRegResult.FORM_VALIDATION_ERROR) {
          const errorFields: string = registerToRaceResponse.competitors?.map((competitor: CompetitorRegistrationResultDto) => {
            return competitor.formFieldErrors?.map((error: FormFieldError) => this.translocoService.translate("app.common." + error.fieldName)).join(", ")
          }).join(", ") ?? "";
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("app.register.to.race.error.form") + " " + errorFields);
        } else {
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("app.register.to.race.error.11"));
        }

      }, (error: RegisterToRaceError) => {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("app.register.to.race.error." + error));
      });
    } else {
      this.toastService.showToast(TOAST_STATE.success,
        this.translocoService.translate("app.register.to.race.error.preview.warning"))
    }
  }

  goToRegistrationInfo(): void {
    this.registerToRaceFormType = RegisterToRaceFormType.REGISTRATION_INFO
  }

  addNewRacer() {
    let allRegistrationItemsAreValid: boolean = true;
    if (this.registerToRaceComponentItems && this.registerToRaceComponentItems.length > 0) {
      for (let i: number = 0; i < this.registerToRaceComponentItems.length; i++) {
        let raceCommercialItem: RaceCommercialItem | undefined = this.registrations[i].raceCommercialItem;
        let registerToRaceItem: RegisterToRaceItemComponent | undefined = this.registerToRaceComponentItems.get(i);
        if (!!registerToRaceItem) {
          registerToRaceItem.onRegister();
          if (!registerToRaceItem.inputForm.valid) {
            allRegistrationItemsAreValid = false;
            break;
          }
          /// We don't want to overwrite race commercial item in parent component because there is a chance that
          // coupon is already applied
          this.registrations[i] = {...registerToRaceItem.competitorRegistration};
          if (raceCommercialItem) {
            this.registrations[i].raceCommercialItem = raceCommercialItem;
          }
        }
      }
    }
    if (allRegistrationItemsAreValid) {
      this.registerHelper?.addCompetitorRegistration();
      this.addCoupon(this.registrations.length - 1);
    }
  }

  onChangeRace(raceId: number) {
    this.router.navigate([this.router.url.split('?')[0]], {
      queryParams: {raceId: raceId},
      queryParamsHandling: "merge",
      replaceUrl: true
    });
  }

  onChangeRegisterToRaceForMyself(registerToRaceForMyself: boolean) {
    this.router.navigate([this.router.url.split('?')[0]], {
      queryParams: {registerToRaceForMyself: registerToRaceForMyself},
      queryParamsHandling: "merge",
      replaceUrl: true
    });
  }

  removeRacer(): void {
    if (this.registrations.length > 1) {
      this.registrations.pop();
      this.removeCoupon(this.registrations.length - 1);
    }
  }

  isAddNewRacerButtonVisible(): boolean {
    return !!(this.race?.raceSettings?.enableGroupRegistrationPayment && this.registrations.length < (this.race.raceSettings.maxGroupRegisteredParticipants ?? 0)) && (this.registerHelper?.areAllEmailsUnique() ?? true) && !this.registerHelper?.isTeamRegistration();
  }

  isRemoveNewRacerVisible(index: number): boolean {
    return index === 0 ? false : !!(this.race?.raceSettings?.enableGroupRegistrationPayment && this.registrations.length > 1) && !this.registerHelper?.isTeamRegistration();
  }

  setEventCurrencyLabel(currencyId: number | undefined): void {
    const curr: Lookups | undefined = this.currencies.find((currency) => currency.id === currencyId);
    this.eventCurrencyLabel = curr?.name ?? "EUR";
  }

  getSubTotalPrice(): number {
    let total: number = 0;
    if (this.registerToRaceFormType === RegisterToRaceFormType.PAYMENT) {
      total = this.wspayConfirmation?.totalAmount ?? 0;
    } else if (this.registerToRaceFormType === RegisterToRaceFormType.CONFIRMATION) {
      total = this.registerToRaceResponse?.totalAmount ?? 0;
    } else if (this.registerToRaceFormType === RegisterToRaceFormType.REGISTRATION_INFO) {
      total = this.registerHelper!.getSubTotalPrice();
    }
    return total;
  }

  getTotalPrice(): number {
    let total: number = 0;
    if (this.registerToRaceFormType === RegisterToRaceFormType.PAYMENT) {
      total = this.wspayConfirmation?.totalAmountWithDiscount ?? 0;
    } else if (this.registerToRaceFormType === RegisterToRaceFormType.CONFIRMATION) {
      total = this.registerToRaceResponse?.totalAmountWithDiscount ?? 0;
    } else if (this.registerToRaceFormType === RegisterToRaceFormType.REGISTRATION_INFO) {
      total = this.registerHelper!.getTotalPrice();
    }
    return total;
  }

  get coupons(): FormArray {
    return this.inputForm.controls.coupons as FormArray;
  }

  couponsFormControl(i: number): FormGroup {
    return this.coupons.controls.at(i) as FormGroup;
  }

  private addCoupon(index: number): void {
    let formGroup = this.formBuilder.group({
      coupon: [undefined],
      validCoupon: [false]
    });
    this.subscriptions.push(formGroup.controls.coupon.valueChanges.pipe(pairwise()).subscribe(([oldValue, newValue]) => {
      if (this.coupons.at(index).get('coupon')?.valid === false && oldValue !== newValue) {
        this.coupons.at(index).get('validCoupon')?.setValue(undefined);
        this.coupons.at(index).get('coupon')?.setValidators([]);
        this.coupons.at(index).get('coupon')?.updateValueAndValidity();
      }
      this.registerHelper!.setDefaultRegistrationFee(index);
      this.registerHelper!.setDefaultAdditionalPricingItems(index);
    }));
    this.coupons.push(formGroup);
    this.inputForm.updateValueAndValidity();
  }

  private removeCoupon(index: number): void {
    this.coupons.removeAt(index);
    this.inputForm.updateValueAndValidity();
  }

  isApplyCouponButtonDisabled(index: number, coupon:string | undefined): boolean {
    let applyCouponButtonDisabled: boolean = true;
    let raceCommercialItemsIsSet: boolean = false;
    let emailIsSet: boolean = false;
    let isEmailUnique: boolean = this.registerHelper!.isEmailUnique(index, this.registrations[index].email ?? '');
    if (this.raceCommercialItems.length === 1) {
      raceCommercialItemsIsSet = true;
    } else if (this.registrations[index].raceCommercialItem) {
      raceCommercialItemsIsSet = true;
    }
    if (raceCommercialItemsIsSet && this.registrations[index].email && this.registrations[index].email !== '') {
      emailIsSet = true;
    }
    if (coupon && coupon !== '' && emailIsSet && isEmailUnique) {
      applyCouponButtonDisabled = false;
    }
    return applyCouponButtonDisabled;
  }

  onApplyCoupon(index: number, coupon:string|undefined) {
    let raceRegistrationRequestDto: RaceRegistrationRequestDto = new RaceRegistrationRequestDto(this.race?.id);
    raceRegistrationRequestDto.competitorRegistrations = [this.extractCompetitorRegistrationDto(index, raceRegistrationRequestDto, coupon)];
    this.calculateRaceItemPrices(raceRegistrationRequestDto);
  }

  isApplyTeamCouponButtonDisabled(): boolean {
    let applyTeamCouponButtonDisabled: boolean = false;
    if (this.registerToRaceComponentItems) {
      for (let i = 0; i < this.registerToRaceComponentItems.length; i++) {
        if (this.isApplyCouponButtonDisabled(i, this.inputForm.controls.teamCoupon.value)) {
          applyTeamCouponButtonDisabled = true;
          break;
        }
      }
    }
    return applyTeamCouponButtonDisabled;
  }

  onApplyTeamCoupon() {
    let raceRegistrationRequestDto: RaceRegistrationRequestDto = new RaceRegistrationRequestDto(this.race?.id);
    raceRegistrationRequestDto.teamCoupon = this.inputForm.controls.teamCoupon.value;
    raceRegistrationRequestDto.teamCommercialItem = this.registrations[0].raceCommercialItem;
    raceRegistrationRequestDto.competitorRegistrations = [];
    for (let i = 0; i < this.registerToRaceComponentItems.length; i++) {
      raceRegistrationRequestDto.competitorRegistrations.push(this.extractCompetitorRegistrationDto(i, raceRegistrationRequestDto, this.inputForm.controls.teamCoupon.value));
    }
    this.calculateRaceItemPrices(raceRegistrationRequestDto);
  }

  private calculateRaceItemPrices(raceRegistrationRequestDto: RaceRegistrationRequestDto) {
    this.raceService.calculateRaceItemPrices(raceRegistrationRequestDto, (registerToRaceResponse: RaceRegistrationResultDto) => {
      if (this.registerHelper?.isTeamRegistration()) {
        if (registerToRaceResponse.teamCommercialItemWithDiscount && registerToRaceResponse.teamCommercialItemWithDiscount.couponOK === true && registerToRaceResponse.teamCommercialItemWithDiscount.commercialItem) {
          this.registerHelper!.teamCommercialItemWithDiscount = registerToRaceResponse.teamCommercialItemWithDiscount;
          this.inputForm.controls.validTeamCoupon.setValue(raceRegistrationRequestDto.teamCoupon);
          this.inputForm.controls.validTeamCoupon.updateValueAndValidity();
          for (let item of raceRegistrationRequestDto!.competitorRegistrations!) {
            let index: number | undefined = this.registerHelper?.findIndexByEmail(item.email!);
            if (index !== undefined) {
              let resultItem: any = registerToRaceResponse?.competitors?.find((result) => result.email === item.email);
              if (resultItem && resultItem.commercialItemWithDiscount?.couponOK === true && resultItem.commercialItemWithDiscount.commercialItem) {
                this.registerHelper!.setCalculatedAdditionalPricingItems(index, resultItem.additionalItemsWithDiscount);
              }
            }
          }
        } else {
          this.registerHelper!.teamCommercialItemWithDiscount = undefined;
          this.inputForm.controls.validTeamCoupon.setValue(undefined);
          this.inputForm.controls.teamCoupon.setValidators([couponEmptyValidator()]);
          this.inputForm.controls.teamCoupon.updateValueAndValidity();
        }
      } else {
        for (let item of raceRegistrationRequestDto!.competitorRegistrations!) {
          let index: number | undefined = this.registerHelper?.findIndexByEmail(item.email!);
          if (index !== undefined) {
            let resultItem = registerToRaceResponse?.competitors?.find((result) => result.email === item.email);
            if (resultItem && resultItem.commercialItemWithDiscount?.couponOK === true && resultItem.commercialItemWithDiscount.commercialItem) {
              this.registerHelper!.setCalculatedRegistrationFee(index, resultItem.commercialItemWithDiscount.commercialItem);
              this.registerHelper!.setCalculatedAdditionalPricingItems(index, resultItem.additionalItemsWithDiscount);
              this.coupons.at(index).get('validCoupon')?.setValue(this.coupons.at(index).get('coupon')?.value);
              this.coupons.at(index).get('validCoupon')?.updateValueAndValidity();
            } else {
              this.coupons.at(index).get('validCoupon')?.setValue(undefined);
              this.coupons.at(index).get('coupon')?.setValidators([couponEmptyValidator()]);
              this.coupons.at(index).get('coupon')?.updateValueAndValidity();
            }
          }
        }
      }
    }, () => {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("app.register.to.race.apply.coupon.error"));
    });
  }

  private extractCompetitorRegistrationDto(index: number, raceRegistrationRequestDto: RaceRegistrationRequestDto, coupon:string|undefined) {
    let item: CompetitorRegistrationDto = this.registrations[index];
    let competitorRegistrationDto: CompetitorRegistrationDto = new CompetitorRegistrationDto();
    competitorRegistrationDto.email = item.email;
    competitorRegistrationDto.couponCode = coupon;
    competitorRegistrationDto.firstName = item.firstName;
    competitorRegistrationDto.lastName = item.lastName;
    competitorRegistrationDto.gender = item.gender;
    competitorRegistrationDto.birthday = item.birthday;
    competitorRegistrationDto.raceCommercialItem = item.raceCommercialItem;
    competitorRegistrationDto.raceAdditionalPricingItems = item.raceAdditionalPricingItems;
    raceRegistrationRequestDto.receiptEmail = item.email;
    return competitorRegistrationDto;
  }

  recalculateValidCoupons() {
    let raceRegistrationRequestDto: RaceRegistrationRequestDto = new RaceRegistrationRequestDto(this.race?.id);
    if (this.registerHelper?.isTeamRegistration()) {
      if (this.inputForm.controls.validTeamCoupon.value && this.inputForm.controls.validTeamCoupon.value === this.inputForm.controls.teamCoupon.value && !this.isApplyTeamCouponButtonDisabled()) {
        raceRegistrationRequestDto.teamCoupon = this.inputForm.controls.teamCoupon.value;
        let registrations: CompetitorRegistrationDto[] = [];
        for (let i = 0; i < this.registerToRaceComponentItems.length; i++) {
          registrations.push(this.extractCompetitorRegistrationDto(i, raceRegistrationRequestDto, this.inputForm.controls.teamCoupon.value));
        }
        raceRegistrationRequestDto.competitorRegistrations = registrations;
        this.calculateRaceItemPrices(raceRegistrationRequestDto);
      }
    } else {
      let registrations: CompetitorRegistrationDto[] = [];
      for (let i = 0; i < this.coupons.length; i++) {
        if (this.coupons.at(i).get('validCoupon')?.value && this.coupons.at(i).get('validCoupon')?.value === this.coupons.at(i).get('coupon')?.value && !this.isApplyCouponButtonDisabled(i,this.coupons.at(i).value.coupon)) {
          registrations.push(this.extractCompetitorRegistrationDto(i, raceRegistrationRequestDto, this.coupons.at(i).value.coupon));
        }
      }
      raceRegistrationRequestDto.competitorRegistrations = registrations;
      if (raceRegistrationRequestDto.competitorRegistrations.length > 0) {
        this.calculateRaceItemPrices(raceRegistrationRequestDto);
      }
    }
  }

  onClose() {
    this.router.navigate([this.authenticationService.isAuthenticated() ? PRIVATE_EVENT_CALENDAR_ROUTE : LANDING_ROUTE]);
  }

  getSummaryCompetitorItems(): CompetitorRegistrationDto[] | CompetitorInfoDTO[] | undefined {
    if (this.registerToRaceFormType === RegisterToRaceFormType.REGISTRATION_INFO || this.registerToRaceFormType === RegisterToRaceFormType.CONFIRMATION) {
      return this.registrations;
    } else {
      return this.wspayConfirmation?.competitors;
    }
  }

  protected readonly RegisterToRaceFormType = RegisterToRaceFormType;
  protected readonly WsPaymentStatus = PaymentStatus;
  protected readonly FormGroup = FormGroup;
  protected readonly Date = Date;
  protected readonly EventPaymentType = EventPaymentType;
  protected readonly CurrentItemType = CurrentItemType;
}

export enum RegisterToRaceComponentState {
  INITIAL,
  ERROR
}

export enum RegisterToRaceUserType {
  PREVIEW,
  ORGANISER,
  MEMBER,
  GUEST
}

export enum RegisterToRaceFormType {
  REGISTRATION_INFO,
  CONFIRMATION,
  PAYMENT,
  REGISTRATION_WITHOUT_PAYMENT_SUCCESS,
}

export class RegisterHelper {
  registrations: CompetitorRegistrationDto[] = [new CompetitorRegistrationDto()];
  raceCommercialItems: RaceCommercialItem[] = [];
  raceAdditionalItems: RaceAdditionalPricingItem[] = [];
  currentRaceCommercialItem?: RaceCommercialItem;
  registerToRaceComponent?: RegisterToRaceComponent;
  // in case of team registration this member holds calculated registration fee price
  teamCommercialItemWithDiscount?: CommercialItemDiscount;

  constructor(raceCommercialItems: RaceCommercialItem[], raceAdditionalItems: RaceAdditionalPricingItem[], registerToRaceComponent?: RegisterToRaceComponent) {
    this.raceCommercialItems = raceCommercialItems;
    this.raceAdditionalItems = raceAdditionalItems;
    this.registerToRaceComponent = registerToRaceComponent;
    if (this.isTeamRegistration()) {
      this.registrations = [];
      for (let i = 0; i < (this.registerToRaceComponent?.race?.raceSettings?.teamType ?? 0); i++) {
        this.registrations.push(new CompetitorRegistrationDto());
      }
    }
    if ((this.raceCommercialItems.length ?? 0) === 1) {
      this.currentRaceCommercialItem = this.raceCommercialItems[0];
      this.registrations.forEach((item: CompetitorRegistrationDto) => item.raceCommercialItem = this.currentRaceCommercialItem);
    }
  }

  onChangeFirstName(firstName: string, index: number) {
    this.registrations[index].firstName = firstName;
  }

  onChangeLastName(lastName: string, index: number) {
    this.registrations[index].lastName = lastName;
  }

  onChangeRegistrationFeeId(registrationFeeId: number): void {
    if (this.raceCommercialItems && this.raceCommercialItems.length > 1) {
      let raceCommercialItem: RaceCommercialItem | undefined = this.raceCommercialItems.find((item: RaceCommercialItem) => item.id === registrationFeeId);
      if (raceCommercialItem) {
        this.currentRaceCommercialItem = raceCommercialItem;
        this.registrations.forEach((item: CompetitorRegistrationDto) => item.raceCommercialItem = this.currentRaceCommercialItem);
        this.registerToRaceComponent!.recalculateValidCoupons();
      }
    }
  }

  onChangeRaceAdditionalItems(ids: {
    raceAdditionalPricingItem: number;
    racePricingItemValue: number | undefined,
    addWithoutPricingItemValues: boolean
  }[], index: number) {
    let raceAdditionalItems: RaceAdditionalPricingItem[] = [];
    for (let idItem of ids) {

      let raceAdditionalPricingItem: RaceAdditionalPricingItem | undefined = this.raceAdditionalItems.find((item: RaceAdditionalPricingItem) => item.id === idItem.raceAdditionalPricingItem);
      if (raceAdditionalPricingItem) {
        if (!idItem.addWithoutPricingItemValues) {
          let pricingItem: RaceAdditionalPricingItem | undefined = raceAdditionalPricingItem?.pricingItemValues?.find((item: RaceCommercialItem) => item.id === idItem.racePricingItemValue);
          if (pricingItem) {
            let raceAdditionalPricingItemTemp: RaceAdditionalPricingItem = {...raceAdditionalPricingItem};
            raceAdditionalPricingItemTemp.pricingItemValues = [pricingItem];
            raceAdditionalItems.push(raceAdditionalPricingItemTemp);
          }
        } else {
          let raceAdditionalPricingItemTemp: RaceAdditionalPricingItem = {...raceAdditionalPricingItem};
          raceAdditionalItems.push(raceAdditionalPricingItemTemp);
        }
      }
    }
    this.registrations[index].raceAdditionalPricingItems = raceAdditionalItems;
    this.registerToRaceComponent!.recalculateValidCoupons();
  }

  onChangeEmail(email: string, index: number): void {
    this.registrations[index].email = email;
  }

  isEmailUnique(index: number, email: string): boolean {
    let isEmailUnique: boolean = true;
    if (this.registrations.length > 1 && email && email !== '') {
      for (let i = 0; i < this.registrations.length; i++) {
        if (i !== index && this.registrations[i].email === email) {
          isEmailUnique = false;
          break;
        }
      }
    }
    return isEmailUnique;
  }

  areAllEmailsUnique(): boolean {
    let areAllEmailsUnique: boolean = true;
    if (this.registrations.length > 1) {
      for (let i = 0; i < this.registrations.length; i++) {
        for (let j = i; j < this.registrations.length; j++) {
          if (this.registrations[i].email === this.registrations[j].email) {
            areAllEmailsUnique = false;
            break;
          }
        }
      }
    }
    return areAllEmailsUnique;
  }

  addCompetitorRegistration() {
    let competitorRegistrationDto: CompetitorRegistrationDto = new CompetitorRegistrationDto();
    competitorRegistrationDto.raceCommercialItem = {...this.currentRaceCommercialItem};
    this.registrations.push(competitorRegistrationDto);
  }

  setDefaultRegistrationFee(index: number) {
    this.registrations[index].raceCommercialItem = this.currentRaceCommercialItem;
  }

  findIndexByEmail(email: string) {
    return this.registrations.findIndex((item: CompetitorRegistrationDto) => item.email === email);
  }

  setDefaultAdditionalPricingItems(index: number) {
    if (this.registrations[index].raceAdditionalPricingItems) {
      let tempRaceAdditionalPricingItems: RaceAdditionalPricingItem[] = [...this.registrations[index].raceAdditionalPricingItems!];
      this.registrations[index].raceAdditionalPricingItems = [];
      for (let currentItem of tempRaceAdditionalPricingItems) {
        for (let defaultItem of this.raceAdditionalItems) {
          if (currentItem.id === defaultItem.id) {
            let tempDefaultItem = {...defaultItem};
            tempDefaultItem.pricingItemValues = [];
            this.registrations[index].raceAdditionalPricingItems!.push(tempDefaultItem);
            if (currentItem.pricingItemValues && defaultItem.pricingItemValues) {
              for (let currentPricingItem of currentItem!.pricingItemValues) {
                for (let defaultPricingItem of defaultItem!.pricingItemValues) {
                  if (currentPricingItem.id === defaultPricingItem.id) {
                    tempDefaultItem.pricingItemValues.push(defaultPricingItem);
                    break;
                  }
                }
              }
            }
          }
        }
      }
      this.registrations[index].raceAdditionalPricingItems!.forEach((item: RaceAdditionalPricingItem) => {
        item.priceWithDiscount = item.price;
      });
    }
  }

  setCalculatedRegistrationFee(index: number, raceCommercialItem: RaceCommercialItem) {
    this.registrations[index].raceCommercialItem = raceCommercialItem;
  }

  setCalculatedAdditionalPricingItems(index: number, additionalItemDiscounts: AdditionalItemDiscount[] | undefined) {
    if (this.registrations[index].raceAdditionalPricingItems && additionalItemDiscounts) {
      let tempRaceAdditionalPricingItems: RaceAdditionalPricingItem[] = [...this.registrations[index].raceAdditionalPricingItems!];
      this.registrations[index].raceAdditionalPricingItems = [];
      for (let currentItem of tempRaceAdditionalPricingItems) {
        for (let calculatedItem of additionalItemDiscounts) {
          if (currentItem.id === calculatedItem?.additionalPricingItem?.id) {
            let tempDefaultItem = {...calculatedItem.additionalPricingItem};
            tempDefaultItem.pricingItemValues = [];
            this.registrations[index].raceAdditionalPricingItems!.push(tempDefaultItem);
            if (currentItem.pricingItemValues && calculatedItem.additionalPricingItem?.pricingItemValues) {
              for (let currentPricingItem of currentItem!.pricingItemValues) {
                for (let calculatedPricingItem of calculatedItem!.additionalPricingItem?.pricingItemValues) {
                  if (currentPricingItem.id === calculatedPricingItem.id) {
                    tempDefaultItem.pricingItemValues.push(calculatedPricingItem);
                    break;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  getTotalPrice(): number {
    let total: number = 0;
    if (this.registrations.length > 0) {
      for (let registration of this.registrations) {
        if (!this.isTeamRegistration()) {
          if (registration.raceCommercialItem && registration.raceCommercialItem.priceWithDiscount) {
            total = total + registration.raceCommercialItem.priceWithDiscount;
          } else {
            total = total + (registration.raceCommercialItem?.price ?? 0);
          }
        }
        if ((registration.raceAdditionalPricingItems?.length ?? 0) > 0) {
          for (let raceAdditionalPricingItem of registration!.raceAdditionalPricingItems!) {
            if ((raceAdditionalPricingItem.pricingItemValues?.length ?? 0) > 0) {
              total = total + (raceAdditionalPricingItem!.pricingItemValues![0].priceWithDiscount ? (raceAdditionalPricingItem!.pricingItemValues![0].priceWithDiscount ?? 0) : (raceAdditionalPricingItem!.pricingItemValues![0].price ?? 0));
            } else {
              total = total + (raceAdditionalPricingItem!.priceWithDiscount ? (raceAdditionalPricingItem!.priceWithDiscount ?? 0) : (raceAdditionalPricingItem!.price ?? 0));
            }
          }
        }
      }
      // if this is a team registration then there is a single registration fee for team registration but we put fee
      // on every competitor so that code is simpler. here we take fee from first competitor in case of team
      // registration
      if (this.isTeamRegistration()) {
          if (this.teamCommercialItemWithDiscount && this.teamCommercialItemWithDiscount.commercialItem?.priceWithDiscount) {
            total = total + this.teamCommercialItemWithDiscount.commercialItem?.priceWithDiscount;
        } else {
          total = total + (this.registrations[0].raceCommercialItem?.price ?? 0);
        }
      }
    }
    return total;
  }

  getSubTotalPrice(): number {
    let total: number = 0;
    if (this.registrations.length > 0) {
      for (let registration of this.registrations) {
        if (!this.isTeamRegistration()) {
          if (registration.raceCommercialItem?.price) {
            total = total + registration.raceCommercialItem.price;
          }
        }
        if ((registration.raceAdditionalPricingItems?.length ?? 0) > 0) {
          for (let raceAdditionalPricingItem of registration!.raceAdditionalPricingItems!) {
            if ((raceAdditionalPricingItem.pricingItemValues?.length ?? 0) > 0) {
              total = total + (raceAdditionalPricingItem!.pricingItemValues![0].price ?? 0);
            } else {
              total = total + (raceAdditionalPricingItem!.price ?? 0);
            }
          }
        }
      }
      // if this is a team registration then there is a single registration fee for team registration but we put fee
      // on every competitor so that code is simpler. here we take fee from first competitor in case of team
      // registration
      if (this.isTeamRegistration()) {
        if (this.registrations[0].raceCommercialItem?.price) {
          total = total + this.registrations[0].raceCommercialItem?.price;
        }
      }
    }
    return total;
  }

  // Check if this is a team registration
  isTeamRegistration(): boolean {
    let isTeamRegistration: boolean = false;
    if (this.registerToRaceComponent?.race?.raceSettings?.teamType && this.registerToRaceComponent?.race?.raceSettings?.teamType !== 1) {
      isTeamRegistration = true;
    }
    return isTeamRegistration;
  }
}

export function couponEmptyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value;
    const errors: ValidationErrors = {};

    // Check length
    if (value.length > 0) {
      errors['couponInvalid'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}
